import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Edit, 
  Trash2, 
  CheckCircle, 
  AlertTriangle, 
  Clock,
  Plus,
  Filter,
  Search
} from 'lucide-react';
import { format, isAfter, isBefore, startOfDay, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatCurrency';

const FixedExpenseList = ({ 
  expenses = [], 
  onEdit, 
  onDelete, 
  onMarkAsPaid, 
  onAdd,
  loading = false,
  readOnly = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, paid, overdue
  const [sortBy, setSortBy] = useState('dueDate'); // dueDate, name, amount
  
  const categories = [
    'Energia',
    'Água', 
    'Internet',
    'Telefone',
    'Aluguel',
    'Segurança',
    'Limpeza',
    'Manutenção',
    'Outros'
  ];
  
  // Função para calcular próximo vencimento
  const getNextDueDate = (dueDay) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Tenta criar data no mês atual
    let nextDue = new Date(currentYear, currentMonth, dueDay);
    
    // Se já passou, vai para o próximo mês
    if (isBefore(nextDue, startOfDay(today))) {
      nextDue = new Date(currentYear, currentMonth + 1, dueDay);
    }
    
    return nextDue;
  };
  
  // Função para verificar se está vencido
  const isOverdue = (expense) => {
    const nextDue = getNextDueDate(expense.dueDay);
    const today = startOfDay(new Date());
    
    // Verifica se não foi pago no mês atual
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const monthKey = `${currentYear}-${currentMonth + 1}`;
    
    const isPaidThisMonth = expense.paymentHistory?.some(payment => {
      const paymentDate = new Date(payment.paidAt);
      return paymentDate.getMonth() === currentMonth && 
             paymentDate.getFullYear() === currentYear;
    });
    
    return isBefore(nextDue, today) && !isPaidThisMonth;
  };
  
  // Função para verificar se precisa de alerta
  const needsAlert = (expense) => {
    const nextDue = getNextDueDate(expense.dueDay);
    const today = startOfDay(new Date());
    const alertDate = addDays(today, expense.alertDaysBefore || 3);
    
    // Verifica se não foi pago no mês atual
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const isPaidThisMonth = expense.paymentHistory?.some(payment => {
      const paymentDate = new Date(payment.paidAt);
      return paymentDate.getMonth() === currentMonth && 
             paymentDate.getFullYear() === currentYear;
    });
    
    return !isPaidThisMonth && (isAfter(alertDate, nextDue) || isAfter(today, nextDue));
  };
  
  // Função para verificar se foi pago no mês atual
  const isPaidThisMonth = (expense) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return expense.paymentHistory?.some(payment => {
      const paymentDate = new Date(payment.paidDate);
      return paymentDate.getMonth() === currentMonth && 
             paymentDate.getFullYear() === currentYear;
    });
  };
  
  // Filtrar e ordenar despesas
  const filteredExpenses = expenses
    .filter(expense => {
      // Filtro por termo de busca
      const matchesSearch = expense.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (expense.description && expense.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Filtro por categoria
      const matchesCategory = !filterCategory || expense.category === filterCategory;
      
      // Filtro por status
      let matchesStatus = true;
      if (filterStatus === 'pending') {
        matchesStatus = !isPaidThisMonth(expense) && !isOverdue(expense);
      } else if (filterStatus === 'paid') {
        matchesStatus = isPaidThisMonth(expense);
      } else if (filterStatus === 'overdue') {
        matchesStatus = isOverdue(expense);
      }
      
      return matchesSearch && matchesCategory && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'dueDate') {
        return a.dueDay - b.dueDay;
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'amount') {
        return b.amount - a.amount;
      }
      return 0;
    });
  
  
  const getStatusIcon = (expense) => {
    if (isPaidThisMonth(expense)) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    } else if (isOverdue(expense)) {
      return <AlertTriangle className="w-5 h-5 text-red-500" />;
    } else if (needsAlert(expense)) {
      return <Clock className="w-5 h-5 text-yellow-500" />;
    }
    return <Calendar className="w-5 h-5 text-gray-400" />;
  };
  
  const getStatusText = (expense) => {
    if (isPaidThisMonth(expense)) {
      return 'Pago este mês';
    } else if (isOverdue(expense)) {
      return 'Vencido';
    } else if (needsAlert(expense)) {
      return 'Próximo ao vencimento';
    }
    return `Vence dia ${expense.dueDay}`;
  };
  
  const getStatusColor = (expense) => {
    if (isPaidThisMonth(expense)) {
      return 'text-green-600 bg-green-50';
    } else if (isOverdue(expense)) {
      return 'text-red-600 bg-red-50';
    } else if (needsAlert(expense)) {
      return 'text-yellow-600 bg-yellow-50';
    }
    return 'text-gray-600 bg-gray-50';
  };
  
  return (
    <div className="space-y-6">
      {/* Header com botão de adicionar */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Despesas Fixas ({filteredExpenses.length})
        </h3>
        {!readOnly && (
          <button
            onClick={onAdd}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nova Despesa
          </button>
        )}
      </div>
      
      {/* Filtros */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-wrap gap-4">
            {/* Busca */}
            <div className="form-group flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar despesas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
            
            {/* Filtro por categoria */}
            <div className="form-group flex-1 min-w-[150px]">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="form-select"
              >
                <option value="">Todas as categorias</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Filtro por status */}
            <div className="form-group flex-1 min-w-[150px]">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-select"
              >
                <option value="all">Todos os status</option>
                <option value="pending">Pendentes</option>
                <option value="paid">Pagos</option>
                <option value="overdue">Vencidos</option>
              </select>
            </div>
            
            {/* Ordenação */}
            <div className="form-group flex-1 min-w-[180px]">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-select"
              >
                <option value="dueDate">Ordenar por vencimento</option>
                <option value="name">Ordenar por nome</option>
                <option value="amount">Ordenar por valor</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Lista de despesas */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredExpenses.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Nenhuma despesa encontrada</p>
          {!readOnly && <p className="text-sm">Adicione uma nova despesa fixa para começar</p>}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredExpenses.map(expense => (
            <div
              key={expense._id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(expense)}
                    <div>
                      <h4 className="font-medium text-gray-900">{expense.name}</h4>
                      <p className="text-sm text-gray-500">{expense.category}</p>
                    </div>
                  </div>
                  
                  {expense.description && (
                    <p className="text-sm text-gray-600 mb-2">{expense.description}</p>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-medium text-gray-900">
                      {formatCurrency(expense.amount)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(expense)}`}>
                      {getStatusText(expense)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {!readOnly && !isPaidThisMonth(expense) && (
                    <button
                      onClick={() => onMarkAsPaid && onMarkAsPaid(expense)}
                      className="btn btn-success p-2"
                      title="Marcar como pago"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  {!readOnly && (
                    <>
                      <button
                        onClick={() => onEdit && onEdit(expense)}
                        className="btn btn-primary p-2"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete && onDelete(expense)}
                        className="btn btn-danger p-2"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FixedExpenseList;